import type { Attribution, SharedAttribution } from './types';

const STORAGE_KEY = 'vlt_attribution';
const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'yclid',
] as const;

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
type QueryAttribution = Partial<Record<AttributionKey, string>>;

function readStored(): Attribution | null {
  try {
    const storage = window.localStorage;
    const value = storage.getItem(STORAGE_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as Attribution;
    return typeof parsed.captured_at === 'string' && typeof parsed.updated_at === 'string'
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function saveStored(value: Attribution): void {
  try {
    const storage = window.localStorage;
    storage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage can fail in private mode. Attribution is optional for boot.
  }
}

function pickQueryAttribution(): QueryAttribution {
  const params = new URL(window.location.href).searchParams;
  const picked: QueryAttribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (value) picked[key] = value;
  }
  return picked;
}

function hasAnyValue(value: QueryAttribution): boolean {
  return Object.keys(value).length > 0;
}

function applyFields(
  target: Attribution,
  prefix: 'first' | 'last',
  values: QueryAttribution,
): void {
  for (const key of ATTRIBUTION_KEYS) {
    const value = values[key];
    if (value) target[`${prefix}_${key}`] = value;
  }
}

export function captureAttribution(
  now: () => string = () => new Date().toISOString(),
): Attribution {
  const next = pickQueryAttribution();
  const stored = readStored();

  if (!hasAnyValue(next) && stored) return stored;

  const timestamp = now();
  const merged: Attribution = stored ?? {
    captured_at: timestamp,
    updated_at: timestamp,
  };

  if (!hasAnyValue(next)) {
    saveStored(merged);
    return merged;
  }

  if (!stored) {
    applyFields(merged, 'first', next);
    merged.first_landing_page = window.location.href;
    merged.first_referrer = document.referrer || '';
  }

  applyFields(merged, 'last', next);
  merged.last_landing_page = window.location.href;
  merged.last_referrer = document.referrer || '';
  merged.updated_at = timestamp;

  saveStored(merged);
  return merged;
}

export function getAttribution(): Attribution | null {
  return readStored();
}

export function toSharedAttribution(attribution: Attribution | null): SharedAttribution {
  return {
    utm_source: attribution?.last_utm_source,
    utm_medium: attribution?.last_utm_medium,
    utm_campaign: attribution?.last_utm_campaign,
    utm_content: attribution?.last_utm_content,
    utm_term: attribution?.last_utm_term,
    gclid: attribution?.last_gclid,
    fbclid: attribution?.last_fbclid,
    yclid: attribution?.last_yclid,
  };
}
